import { useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Package, X } from 'lucide-react'

export function ProductPicker({ workspaceId, onSelect, onClose }: { workspaceId: string; onSelect: (p: any) => void; onClose: () => void }) {
  const [search, setSearch] = useState('')
  const { data: products = [] } = useQuery({
    queryKey: ['products', workspaceId],
    queryFn: async () => {
      const { data } = await supabase.from('products').select('*').eq('workspace_id', workspaceId).eq('is_active', true).order('name')
      return data || []
    },
  })
  const filtered = products.filter((p: any) => !search || p.name.toLowerCase().includes(search.toLowerCase()))

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, width: '100%', maxWidth: 500, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Package style={{ width: 16, height: 16, color: 'var(--accent-text)' }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>Add from catalog</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 16, height: 16 }} /></button>
        </div>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..." autoFocus
            style={{ width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ maxHeight: 360, overflow: 'auto' }}>
          {filtered.length === 0 ? (
            <p style={{ padding: '24px', textAlign: 'center', fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>
              {products.length === 0 ? 'No products in catalog yet' : 'No matches'}
            </p>
          ) : filtered.map((p: any) => (
            <button key={p.id} onClick={() => { onSelect(p); onClose() }}
              style={{ width: '100%', padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-dim)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 3px' }}>{p.name}</p>
                {p.description && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{p.description}</p>}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-text)', margin: '0 0 2px' }}>${p.unit_price}</p>
                <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>per {p.unit}</p>
              </div>
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  )
}
